import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, Users, X } from 'lucide-react'
import { Avatar } from '../../../occurrences/components/common/Avatar'
import type { SystemIssueMentionCandidate } from '../../types/system-issues'

interface TagColleaguesPickerProps {
  candidates: SystemIssueMentionCandidate[]
  /** IDs já marcados (chips fixos, não removíveis por aqui — ver `onRemove`). */
  selectedIds: number[]
  onToggle: (candidate: SystemIssueMentionCandidate) => void
  onRemove?: (id: number) => void
  loading?: boolean
  disabled?: boolean
  placeholder?: string
}

/**
 * Seleção múltipla de colegas da empresa (candidatos já vêm
 * pré-filtrados pelo backend — ver `useMentionCandidates`/
 * `useMentionCandidatesForIssue`). Usado tanto no formulário de "novo
 * problema" quanto na thread (`IssueThread`), com fontes de candidatos
 * diferentes mas o mesmo componente de UI.
 */
export function TagColleaguesPicker({
  candidates,
  selectedIds,
  onToggle,
  onRemove,
  loading = false,
  disabled = false,
  placeholder = 'Buscar colega pelo nome…',
}: TagColleaguesPickerProps) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const selected = useMemo(
    () => candidates.filter((c) => selectedIds.includes(c.id)),
    [candidates, selectedIds],
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return candidates
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .slice(0, 8)
  }, [candidates, search])

  return (
    <div ref={containerRef} className="relative">
      {selected.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {selected.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-surface-muted py-0.5 pl-1 pr-2 text-[11px] text-body/80"
            >
              <Avatar
                name={c.name}
                picture={c.google_picture ?? c.microsoft_picture ?? null}
                size={16}
              />
              {c.name}
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(c.id)}
                  aria-label={`Remover marcação de ${c.name}`}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-surface"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search
          className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-subtle"
          aria-hidden
        />
        <input
          type="text"
          value={search}
          disabled={disabled}
          onChange={(e) => {
            setSearch(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full rounded-[5px] border border-border-subtle bg-surface py-1.5 pl-8 pr-3 text-xs text-body placeholder:text-subtle focus:border-[#2563eb] focus:outline-none disabled:opacity-50"
        />
      </div>

      {open && (
        <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-[5px] border border-border-subtle bg-surface shadow-lg">
          {loading && (
            <p className="px-3 py-2 text-xs text-subtle">Carregando…</p>
          )}
          {!loading && candidates.length === 0 && (
            <p className="flex items-center gap-1.5 px-3 py-2 text-xs text-subtle">
              <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Nenhum colega ativo encontrado na empresa.
            </p>
          )}
          {!loading && candidates.length > 0 && filtered.length === 0 && (
            <p className="px-3 py-2 text-xs text-subtle">
              Nenhum colega encontrado.
            </p>
          )}
          {!loading &&
            filtered.map((c) => {
              const isSelected = selectedIds.includes(c.id)
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onToggle(c)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition hover:bg-surface-muted ${
                    isSelected ? 'bg-[#eff6ff] dark:bg-blue-900/20' : ''
                  }`}
                >
                  <Avatar
                    name={c.name}
                    picture={c.google_picture ?? c.microsoft_picture ?? null}
                    size={20}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body">{c.name}</span>
                    <span className="block truncate text-[11px] text-muted">
                      {c.role}
                    </span>
                  </span>
                  {isSelected && (
                    <span className="shrink-0 text-[10px] font-medium text-[#2563eb] dark:text-blue-400">
                      Marcado
                    </span>
                  )}
                </button>
              )
            })}
        </div>
      )}
    </div>
  )
}
